import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  Stack,
  Chip,
  Button,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  InputAdornment,
  Skeleton,
  Paper,
  Avatar,
  Tooltip,
  Breadcrumbs,
  Link,
  CircularProgress,
  Divider,
  alpha,
} from "@mui/material";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import ElectricBoltRoundedIcon from "@mui/icons-material/ElectricBoltRounded";
import SmartphoneRoundedIcon from "@mui/icons-material/SmartphoneRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import SettingsInputAntennaRoundedIcon from "@mui/icons-material/SettingsInputAntennaRounded";
import WaterDropRoundedIcon from "@mui/icons-material/WaterDropRounded";
import PropaneTankRoundedIcon from "@mui/icons-material/PropaneTankRounded";
import GasMeterRoundedIcon from "@mui/icons-material/GasMeterRounded";
import WifiRoundedIcon from "@mui/icons-material/WifiRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import TollRoundedIcon from "@mui/icons-material/TollRounded";
import HealthAndSafetyRoundedIcon from "@mui/icons-material/HealthAndSafetyRounded";
import RequestQuoteRoundedIcon from "@mui/icons-material/RequestQuoteRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import LiveTvRoundedIcon from "@mui/icons-material/LiveTvRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import {
  getBbpsCategories,
  getBbpsBillers,
  fetchBbpsBill,
  payBbpsBill,
  listBbpsPayments,
  refreshBbpsPayment,
  getMyBbpsCharge,
} from "../api";

/* ---------------------------------- helpers ---------------------------------- */

const formatINR = (v) =>
  v == null || v === "" || isNaN(Number(v))
    ? "—"
    : `₹${Number(v).toLocaleString("en-IN")}`;

const formatDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt)
    ? "—"
    : dt.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

/* Map category keys/labels to an icon + accent colour, with a fallback */
const CATEGORY_META = [
  { match: /electr/i, icon: <ElectricBoltRoundedIcon />, color: "#f59e0b" },
  { match: /prepaid/i, icon: <SmartphoneRoundedIcon />, color: "#22c55e" },
  { match: /postpaid|mobile/i, icon: <PhoneIphoneRoundedIcon />, color: "#0ea5e9" },
  { match: /dth/i, icon: <SettingsInputAntennaRoundedIcon />, color: "#2f8fb5" },
  { match: /water/i, icon: <WaterDropRoundedIcon />, color: "#06b6d4" },
  { match: /lpg/i, icon: <PropaneTankRoundedIcon />, color: "#ef4444" },
  { match: /gas/i, icon: <GasMeterRoundedIcon />, color: "#f97316" },
  { match: /broadband|internet/i, icon: <WifiRoundedIcon />, color: "#1c6b8c" },
  { match: /landline/i, icon: <CallRoundedIcon />, color: "#64748b" },
  { match: /fastag|toll/i, icon: <TollRoundedIcon />, color: "#0891b2" },
  { match: /insurance/i, icon: <HealthAndSafetyRoundedIcon />, color: "#16a34a" },
  { match: /loan/i, icon: <RequestQuoteRoundedIcon />, color: "#d946ef" },
  { match: /credit\s*card/i, icon: <CreditCardRoundedIcon />, color: "#7c3aed" },
  { match: /municipal|tax/i, icon: <AccountBalanceRoundedIcon />, color: "#78716c" },
  { match: /cable|tv/i, icon: <LiveTvRoundedIcon />, color: "#e11d48" },
  { match: /education|fee/i, icon: <SchoolRoundedIcon />, color: "#2563eb" },
];

const categoryMeta = (cat) => {
  const s = `${cat?.key || ""} ${cat?.label || ""}`.replace(/[_-]/g, " ");
  return (
    CATEGORY_META.find((m) => m.match.test(s)) || {
      icon: <ReceiptLongRoundedIcon />,
      color: "#1c6b8c",
    }
  );
};

/* Prettify a BBPS paramName like "Consumer Number" / "consumerNumber" */
const prettyParam = (name = "") =>
  name
    .replace(/[_-]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

const isNumericParam = (p) =>
  /numeric|number|integer|int|digit/i.test(p?.dataType || "");

/* "Exact" (and not "…and above/below") means the fetched amount is locked */
const isExactPayment = (exactness = "") =>
  /exact/i.test(exactness) && !/above|below|any/i.test(exactness);

/* RBI: cash bill payments of ₹50,000 or more require the payer's PAN */
const PAN_THRESHOLD = 50000;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

const maskPan = (pan = "") =>
  pan.length === 10 ? `${pan.slice(0, 5)}****${pan.slice(9)}` : pan;

const STATUS_META = {
  success: { label: "Success", color: "success" },
  failed: { label: "Failed", color: "error" },
  pending: { label: "Pending", color: "warning" },
};

function StatusChip({ status }) {
  const meta = STATUS_META[status] || { label: status || "—", color: "default" };
  return (
    <Chip
      size="small"
      label={meta.label}
      color={meta.color}
      sx={{ fontWeight: 600, fontSize: 11 }}
    />
  );
}

/* input_params may arrive as an array, a JSON string, or null */
const parseParams = (v) => {
  if (!v) return [];
  let arr = v;
  if (typeof v === "string") {
    try {
      arr = JSON.parse(v);
    } catch {
      return [];
    }
  }
  return Array.isArray(arr) ? arr : [];
};

/* Label / value row for the details dialog — stacks on mobile, two columns on sm+ */
function DetailRow({ label, value, mono }) {
  return (
    <Grid container spacing={0.5} sx={{ py: 0.6 }}>
      <Grid item xs={12} sm={5}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={7}>
        {typeof value === "string" || typeof value === "number" ? (
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              fontFamily: mono ? "monospace" : undefined,
              wordBreak: "break-word",
            }}
          >
            {value}
          </Typography>
        ) : (
          value
        )}
      </Grid>
    </Grid>
  );
}

/* ------------------------------ debounce hook ------------------------------ */

function useDebounced(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/* ================================== page ================================== */

const STEP = { CATEGORY: 0, BILLER: 1, DETAILS: 2, RESULT: 3 };

export default function PayBills() {
  const [step, setStep] = useState(STEP.CATEGORY);
  const [error, setError] = useState(null);

  /* flat convenience charge for the current user's role (0 if none/failed) */
  const [myCharge, setMyCharge] = useState(0);

  /* categories */
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [category, setCategory] = useState(null);

  /* billers */
  const [billers, setBillers] = useState([]);
  const [billersLoading, setBillersLoading] = useState(false);
  const [billerSearch, setBillerSearch] = useState("");
  const [billerPage, setBillerPage] = useState(1);
  const [billerTotal, setBillerTotal] = useState(0);
  const [biller, setBiller] = useState(null);
  const debouncedSearch = useDebounced(billerSearch);

  /* bill details form */
  const [paramValues, setParamValues] = useState({});
  const [amount, setAmount] = useState("");
  const [pan, setPan] = useState("");
  const [fetching, setFetching] = useState(false);
  const [fetchedBill, setFetchedBill] = useState(null);
  const [fetchRef, setFetchRef] = useState(null);

  /* pay */
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const [result, setResult] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  /* recent payments */
  const [payments, setPayments] = useState([]);
  const [detailPayment, setDetailPayment] = useState(null);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [paymentsTotal, setPaymentsTotal] = useState(0);

  const billersReqId = useRef(0);

  /* ------------------------------ data loading ------------------------------ */

  useEffect(() => {
    getBbpsCategories()
      .then(({ data }) => setCategories(data.categories || []))
      .catch((e) =>
        setError(e.response?.data?.error || "Failed to load categories"),
      )
      .finally(() => setCategoriesLoading(false));
  }, []);

  /* Load the current role's flat convenience charge; treat failure as 0. */
  useEffect(() => {
    getMyBbpsCharge()
      .then(({ data }) => setMyCharge(Number(data?.charge) || 0))
      .catch(() => setMyCharge(0));
  }, []);

  const loadPayments = useCallback(() => {
    setPaymentsLoading(true);
    listBbpsPayments(paymentsPage)
      .then(({ data }) => {
        setPayments(data.payments || []);
        setPaymentsTotal(data.pagination?.total || 0);
      })
      .catch(() => {})
      .finally(() => setPaymentsLoading(false));
  }, [paymentsPage]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  useEffect(() => {
    if (!category || step !== STEP.BILLER) return;
    const reqId = ++billersReqId.current;
    setBillersLoading(true);
    getBbpsBillers({
      category: category.key,
      search: debouncedSearch || undefined,
      page: billerPage,
      limit: 20,
    })
      .then(({ data }) => {
        if (reqId !== billersReqId.current) return;
        setBillers(data.billers || []);
        setBillerTotal(data.pagination?.total || 0);
      })
      .catch((e) => {
        if (reqId !== billersReqId.current) return;
        setError(e.response?.data?.error || "Failed to load billers");
      })
      .finally(() => {
        if (reqId === billersReqId.current) setBillersLoading(false);
      });
  }, [category, step, debouncedSearch, billerPage]);

  /* ------------------------------- navigation ------------------------------- */

  const resetBillerState = () => {
    setBillers([]);
    setBillerSearch("");
    setBillerPage(1);
    setBillerTotal(0);
    setBiller(null);
  };

  const resetFormState = () => {
    setParamValues({});
    setAmount("");
    setPan("");
    setFetchedBill(null);
    setFetchRef(null);
    setFetching(false);
  };

  const goToCategories = () => {
    setStep(STEP.CATEGORY);
    setCategory(null);
    resetBillerState();
    resetFormState();
    setResult(null);
    setError(null);
  };

  const goToBillers = () => {
    setStep(STEP.BILLER);
    setBiller(null);
    resetFormState();
    setResult(null);
    setError(null);
  };

  const selectCategory = (cat) => {
    setCategory(cat);
    resetBillerState();
    setStep(STEP.BILLER);
    setError(null);
  };

  const selectBiller = (b) => {
    setBiller(b);
    resetFormState();
    setStep(STEP.DETAILS);
    setError(null);
  };

  /* ------------------------------- validation ------------------------------- */

  const inputParams = useMemo(() => biller?.inputParams || [], [biller]);

  const paramError = (p) => {
    const v = (paramValues[p.paramName] || "").trim();
    if (!v) return p.isOptional ? null : "Required";
    if (p.minLength && v.length < Number(p.minLength))
      return `Minimum ${p.minLength} characters`;
    if (p.maxLength && v.length > Number(p.maxLength))
      return `Maximum ${p.maxLength} characters`;
    return null;
  };

  const paramsValid = inputParams.every((p) => !paramError(p));
  const amountLocked = fetchedBill && isExactPayment(biller?.paymentExactness);
  const amountValid = amount !== "" && Number(amount) > 0;
  const panRequired = amountValid && Number(amount) >= PAN_THRESHOLD;
  const panValid = PAN_REGEX.test(pan);
  const panOk = !panRequired || panValid;

  const filledParams = useMemo(() => {
    const out = {};
    inputParams.forEach((p) => {
      const v = (paramValues[p.paramName] || "").trim();
      if (v) out[p.paramName] = v;
    });
    return out;
  }, [inputParams, paramValues]);

  /* --------------------------------- actions --------------------------------- */

  const handleFetchBill = async () => {
    setFetching(true);
    setError(null);
    try {
      const { data } = await fetchBbpsBill(biller.billerId, filledParams);
      setFetchedBill(data.bill || {});
      setFetchRef(data.fetchRef || null);
      if (data.bill?.billAmount != null) setAmount(String(data.bill.billAmount));
    } catch (e) {
      setError(e.response?.data?.error || "Failed to fetch bill");
    }
    setFetching(false);
  };

  const handlePay = async () => {
    setPaying(true);
    setError(null);
    try {
      const { data } = await payBbpsBill({
        billerId: biller.billerId,
        billerName: biller.billerName,
        category: category?.key,
        amount: Number(amount),
        inputParams: filledParams,
        fetchRef: fetchRef || undefined,
        pan: pan || undefined,
      });
      setResult(data);
      setConfirmOpen(false);
      setStep(STEP.RESULT);
      setPaymentsPage(1);
      loadPayments();
    } catch (e) {
      setConfirmOpen(false);
      setError(e.response?.data?.error || "Payment failed. Please try again.");
    }
    setPaying(false);
  };

  const handleRefreshResult = async () => {
    if (!result?.paymentId) return;
    setRefreshing(true);
    try {
      const { data } = await refreshBbpsPayment(result.paymentId);
      setResult((r) => ({ ...r, status: data.status }));
      if (data.status !== "pending") loadPayments();
    } catch (e) {
      setError(e.response?.data?.error || "Failed to refresh status");
    }
    setRefreshing(false);
  };

  const handleRefreshRow = async (id) => {
    try {
      const { data } = await refreshBbpsPayment(id);
      setPayments((rows) =>
        rows.map((r) => (r.id === id ? { ...r, status: data.status } : r)),
      );
    } catch {
      /* ignore */
    }
  };

  /* -------------------------------- renderers -------------------------------- */

  const breadcrumbs = (
    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
      {step > STEP.CATEGORY && step !== STEP.RESULT && (
        <IconButton
          size="small"
          onClick={step === STEP.BILLER ? goToCategories : goToBillers}
          sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.06) }}
        >
          <ArrowBackRoundedIcon fontSize="small" />
        </IconButton>
      )}
      <Breadcrumbs
        separator={<ChevronRightRoundedIcon sx={{ fontSize: 16 }} />}
        sx={{ "& .MuiBreadcrumbs-li": { fontSize: 13 } }}
      >
        <Link
          component="button"
          type="button"
          underline={step === STEP.CATEGORY ? "none" : "hover"}
          color={step === STEP.CATEGORY ? "text.primary" : "text.secondary"}
          onClick={goToCategories}
          sx={{ fontWeight: step === STEP.CATEGORY ? 700 : 500, fontSize: 13 }}
        >
          Categories
        </Link>
        {category && (
          <Link
            component="button"
            type="button"
            underline={step === STEP.BILLER ? "none" : "hover"}
            color={step === STEP.BILLER ? "text.primary" : "text.secondary"}
            onClick={goToBillers}
            sx={{ fontWeight: step === STEP.BILLER ? 700 : 500, fontSize: 13 }}
          >
            {category.label}
          </Link>
        )}
        {biller && step >= STEP.DETAILS && (
          <Typography fontSize={13} fontWeight={700} color="text.primary">
            {biller.billerName}
          </Typography>
        )}
      </Breadcrumbs>
    </Stack>
  );

  const renderCategories = () => (
    <Grid container spacing={2}>
      {categoriesLoading
        ? [...Array(8)].map((_, i) => (
            <Grid item xs={6} sm={4} md={3} key={i}>
              <Skeleton variant="rounded" height={110} sx={{ borderRadius: 3 }} />
            </Grid>
          ))
        : categories.map((cat) => {
            const meta = categoryMeta(cat);
            return (
              <Grid item xs={6} sm={4} md={3} key={cat.key}>
                <Paper
                  variant="outlined"
                  onClick={() => selectCategory(cat)}
                  sx={{
                    p: 2.5,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1.25,
                    cursor: "pointer",
                    borderRadius: 3,
                    textAlign: "center",
                    transition: "all .15s ease",
                    "&:hover": {
                      borderColor: meta.color,
                      bgcolor: alpha(meta.color, 0.04),
                      transform: "translateY(-2px)",
                      boxShadow: `0 8px 20px ${alpha(meta.color, 0.15)}`,
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      color: meta.color,
                      bgcolor: alpha(meta.color, 0.12),
                    }}
                  >
                    {meta.icon}
                  </Avatar>
                  <Typography fontWeight={600} fontSize={13.5} lineHeight={1.3}>
                    {cat.label}
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
      {!categoriesLoading && categories.length === 0 && (
        <Grid item xs={12}>
          <Box sx={{ p: 5, textAlign: "center" }}>
            <ReceiptLongRoundedIcon
              sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
            />
            <Typography color="text.secondary">
              Bill payment categories are unavailable right now
            </Typography>
          </Box>
        </Grid>
      )}
    </Grid>
  );

  const renderBillers = () => (
    <Box>
      <TextField
        fullWidth
        size="small"
        placeholder={`Search ${category?.label || ""} billers…`}
        value={billerSearch}
        onChange={(e) => {
          setBillerSearch(e.target.value);
          setBillerPage(1);
        }}
        autoFocus
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRoundedIcon sx={{ color: "text.disabled" }} />
            </InputAdornment>
          ),
          sx: { borderRadius: 2.5 },
        }}
        sx={{ mb: 2 }}
      />
      {billersLoading ? (
        <Stack spacing={1.25}>
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} variant="rounded" height={64} sx={{ borderRadius: 2.5 }} />
          ))}
        </Stack>
      ) : billers.length === 0 ? (
        <Box sx={{ p: 5, textAlign: "center" }}>
          <SearchRoundedIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
          <Typography fontWeight={600} mb={0.5}>
            No billers found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {billerSearch
              ? "Try a different search term"
              : "No billers available in this category yet"}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.25}>
          {billers.map((b) => {
            const meta = categoryMeta(category);
            return (
              <Paper
                key={b.billerId}
                variant="outlined"
                onClick={() => selectBiller(b)}
                sx={{
                  p: 1.75,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.75,
                  cursor: "pointer",
                  borderRadius: 2.5,
                  transition: "all .15s ease",
                  "&:hover": {
                    borderColor: meta.color,
                    bgcolor: alpha(meta.color, 0.03),
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    color: meta.color,
                    bgcolor: alpha(meta.color, 0.12),
                  }}
                >
                  {meta.icon}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography fontWeight={600} fontSize={14} noWrap>
                    {b.billerName}
                  </Typography>
                  {b.coverage && (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <PublicRoundedIcon
                        sx={{ fontSize: 13, color: "text.disabled" }}
                      />
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {b.coverage}
                      </Typography>
                    </Stack>
                  )}
                </Box>
                <ChevronRightRoundedIcon sx={{ color: "text.disabled" }} />
              </Paper>
            );
          })}
        </Stack>
      )}
      {Math.ceil(billerTotal / 20) > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
          <Pagination
            count={Math.ceil(billerTotal / 20)}
            page={billerPage}
            onChange={(_, v) => setBillerPage(v)}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );

  const renderBillSummary = () => (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        mt: 2.5,
        borderRadius: 3,
        borderColor: (t) => alpha(t.palette.success.main, 0.4),
        bgcolor: (t) => alpha(t.palette.success.main, 0.04),
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
        <CheckCircleRoundedIcon color="success" sx={{ fontSize: 20 }} />
        <Typography fontWeight={700} fontSize={14}>
          Bill fetched
        </Typography>
      </Stack>
      <Grid container spacing={1.5}>
        {[
          ["Customer", fetchedBill.customerName],
          ["Bill Amount", formatINR(fetchedBill.billAmount)],
          ["Due Date", fetchedBill.dueDate ? formatDate(fetchedBill.dueDate) : null],
          ["Bill Date", fetchedBill.billDate ? formatDate(fetchedBill.billDate) : null],
          ["Bill Number", fetchedBill.billNumber],
          ["Bill Period", fetchedBill.billPeriod],
        ]
          .filter(([, v]) => v != null && v !== "" && v !== "—")
          .map(([label, value]) => (
            <Grid item xs={6} key={label}>
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
              <Typography
                fontWeight={label === "Bill Amount" ? 800 : 600}
                fontSize={label === "Bill Amount" ? 17 : 14}
              >
                {value}
              </Typography>
            </Grid>
          ))}
      </Grid>
    </Paper>
  );

  const renderPanField = () =>
    panRequired && (
      <TextField
        fullWidth
        required
        label="Payer PAN"
        value={pan}
        onChange={(e) =>
          setPan(
            e.target.value
              .toUpperCase()
              .replace(/[^A-Z0-9]/g, "")
              .slice(0, 10),
          )
        }
        error={pan !== "" && !panValid}
        helperText={
          pan !== "" && !panValid
            ? "Enter a valid PAN (e.g. ABCDE1234F)"
            : "Required for payments of ₹50,000 or more"
        }
        inputProps={{ maxLength: 10, style: { textTransform: "uppercase" } }}
        InputProps={{ sx: { borderRadius: 2 } }}
      />
    );

  const renderDetails = () => (
    <Box sx={{ maxWidth: 480 }}>
      <Stack spacing={2}>
        {inputParams.map((p) => {
          const err = paramError(p);
          const touched = paramValues[p.paramName] !== undefined;
          return (
            <TextField
              key={p.paramName}
              fullWidth
              label={`${prettyParam(p.paramName)}${p.isOptional ? " (optional)" : ""}`}
              required={!p.isOptional}
              value={paramValues[p.paramName] || ""}
              disabled={!!fetchedBill}
              onChange={(e) => {
                let v = e.target.value;
                if (isNumericParam(p)) v = v.replace(/[^0-9.]/g, "");
                setParamValues((prev) => ({ ...prev, [p.paramName]: v }));
              }}
              error={touched && !!err && (paramValues[p.paramName] || "") !== ""}
              helperText={
                touched && (paramValues[p.paramName] || "") !== "" ? err : undefined
              }
              inputProps={{
                maxLength: p.maxLength ? Number(p.maxLength) : undefined,
                inputMode: isNumericParam(p) ? "numeric" : "text",
              }}
              InputProps={{ sx: { borderRadius: 2 } }}
            />
          );
        })}

        {biller?.fetchRequired ? (
          fetchedBill ? (
            <>
              {renderBillSummary()}
              <TextField
                fullWidth
                label="Amount to pay"
                value={amount}
                disabled={amountLocked}
                onChange={(e) =>
                  setAmount(e.target.value.replace(/[^0-9.]/g, ""))
                }
                helperText={
                  amountLocked
                    ? "This biller requires the exact bill amount"
                    : biller?.paymentExactness
                      ? `Payment: ${biller.paymentExactness}`
                      : undefined
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                  endAdornment: amountLocked ? (
                    <InputAdornment position="end">
                      <LockRoundedIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                    </InputAdornment>
                  ) : undefined,
                  sx: { fontWeight: 700, fontSize: 17, borderRadius: 2 },
                }}
              />
              {renderPanField()}
              <Stack direction="row" spacing={1.5}>
                <Button
                  variant="outlined"
                  onClick={resetFormState}
                  sx={{ borderRadius: 2.5 }}
                >
                  Edit details
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={!amountValid || !panOk}
                  onClick={() => setConfirmOpen(true)}
                  sx={{ borderRadius: 2.5, fontWeight: 700 }}
                >
                  Pay {formatINR(amount)}
                </Button>
              </Stack>
            </>
          ) : (
            <Button
              variant="contained"
              size="large"
              disabled={!paramsValid || fetching}
              onClick={handleFetchBill}
              startIcon={
                fetching ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <ReceiptLongRoundedIcon />
                )
              }
              sx={{ borderRadius: 2.5, fontWeight: 700 }}
            >
              {fetching ? "Fetching bill…" : "Fetch Bill"}
            </Button>
          )
        ) : (
          <>
            <TextField
              fullWidth
              label="Amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
              helperText={
                biller?.paymentExactness
                  ? `Payment: ${biller.paymentExactness}`
                  : undefined
              }
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                sx: { fontWeight: 700, fontSize: 17, borderRadius: 2 },
              }}
            />
            {renderPanField()}
            <Button
              variant="contained"
              size="large"
              disabled={!paramsValid || !amountValid || !panOk}
              onClick={() => setConfirmOpen(true)}
              sx={{ borderRadius: 2.5, fontWeight: 700 }}
            >
              {amountValid ? `Pay ${formatINR(amount)}` : "Pay Bill"}
            </Button>
          </>
        )}
      </Stack>
    </Box>
  );

  const renderResult = () => {
    const status = result?.status || "pending";
    const meta = {
      success: {
        icon: <CheckCircleRoundedIcon sx={{ fontSize: 64 }} color="success" />,
        title: "Payment Successful",
        subtitle: result?.message || "Your bill has been paid.",
      },
      failed: {
        icon: <CancelRoundedIcon sx={{ fontSize: 64 }} color="error" />,
        title: "Payment Failed",
        subtitle: result?.message || "The payment could not be completed.",
      },
      pending: {
        icon: (
          <HourglassTopRoundedIcon sx={{ fontSize: 64, color: "warning.main" }} />
        ),
        title: "Payment Pending",
        subtitle:
          result?.message ||
          "We're waiting for confirmation from the biller. This usually takes a few moments.",
      },
    }[status] || {
      icon: <HourglassTopRoundedIcon sx={{ fontSize: 64, color: "warning.main" }} />,
      title: "Payment Pending",
      subtitle: result?.message || "",
    };

    return (
      <Box sx={{ textAlign: "center", py: 4, maxWidth: 440, mx: "auto" }}>
        {meta.icon}
        <Typography variant="h6" fontWeight={800} mt={1.5}>
          {meta.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5} mb={2}>
          {meta.subtitle}
        </Typography>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 3, textAlign: "left" }}>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Biller
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {biller?.billerName || "—"}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Amount
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {formatINR(amount)}
              </Typography>
            </Stack>
            {result?.bbpsRefId && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  BBPS Ref ID
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontFamily: "monospace" }}
                >
                  {result.bbpsRefId}
                </Typography>
              </Stack>
            )}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Status
              </Typography>
              <StatusChip status={status} />
            </Stack>
          </Stack>
        </Paper>

        <Stack direction="row" spacing={1.5} justifyContent="center">
          {status === "pending" && (
            <Button
              variant="outlined"
              startIcon={
                refreshing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <RefreshRoundedIcon />
                )
              }
              disabled={refreshing}
              onClick={handleRefreshResult}
              sx={{ borderRadius: 2.5 }}
            >
              Refresh Status
            </Button>
          )}
          <Button
            variant="contained"
            onClick={goToCategories}
            sx={{ borderRadius: 2.5, fontWeight: 700 }}
          >
            Pay Another Bill
          </Button>
        </Stack>
      </Box>
    );
  };

  const stepTitles = {
    [STEP.CATEGORY]: "Choose a category",
    [STEP.BILLER]: "Choose a biller",
    [STEP.DETAILS]: biller?.fetchRequired
      ? "Enter details to fetch your bill"
      : "Enter payment details",
    [STEP.RESULT]: null,
  };

  /* ---------------------------------- render ---------------------------------- */

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Pay Bills
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Pay electricity, mobile, DTH, gas & more via BBPS
          </Typography>
        </Box>
        <Chip
          icon={<ReceiptLongRoundedIcon sx={{ fontSize: 16 }} />}
          label="Bharat BillPay"
          size="small"
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      </Stack>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Card sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        {breadcrumbs}
        {stepTitles[step] && (
          <Typography variant="subtitle1" fontWeight={700} mb={2}>
            {stepTitles[step]}
          </Typography>
        )}
        {step === STEP.CATEGORY && renderCategories()}
        {step === STEP.BILLER && renderBillers()}
        {step === STEP.DETAILS && renderDetails()}
        {step === STEP.RESULT && renderResult()}
      </Card>

      {/* Recent payments */}
      <Card sx={{ overflow: "hidden", borderRadius: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: "divider" }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Recent Bill Payments
          </Typography>
          <IconButton size="small" onClick={loadPayments} title="Refresh">
            <RefreshRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
        {paymentsLoading ? (
          <Box sx={{ p: 3 }}>
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} height={40} sx={{ mb: 1 }} />
            ))}
          </Box>
        ) : payments.length === 0 ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <ReceiptLongRoundedIcon
              sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
            />
            <Typography fontWeight={600} mb={0.5}>
              No bill payments yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your bill payment history will appear here
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 820 }}>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    fontWeight: 700,
                    fontSize: 12,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    color: "text.secondary",
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                  },
                }}
              >
                <TableCell>Biller</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>BBPS Ref</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell sx={{ fontWeight: 600, maxWidth: 220 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {p.biller_name || p.biller_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={p.category || "—"}
                      variant="outlined"
                      sx={{ fontSize: 11, fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {formatINR(p.amount)}
                  </TableCell>
                  <TableCell>
                    {p.status === "failed" && p.failure_reason ? (
                      <Tooltip title={p.failure_reason} arrow>
                        <span>
                          <StatusChip status={p.status} />
                        </span>
                      </Tooltip>
                    ) : (
                      <StatusChip status={p.status} />
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "monospace",
                      fontSize: 12,
                      color: "text.secondary",
                    }}
                  >
                    {p.bbps_ref_id || "—"}
                  </TableCell>
                  <TableCell
                    sx={{ color: "text.secondary", fontSize: 13, whiteSpace: "nowrap" }}
                  >
                    {formatDate(p.created_at)}
                  </TableCell>
                  <TableCell align="right">
                    <Stack
                      direction="row"
                      spacing={0.5}
                      justifyContent="flex-end"
                      alignItems="center"
                    >
                      {p.status === "pending" && (
                        <Tooltip title="Check status" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleRefreshRow(p.id)}
                          >
                            <RefreshRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="View details" arrow>
                        <IconButton
                          size="small"
                          onClick={() => setDetailPayment(p)}
                        >
                          <VisibilityRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            </Table>
          </Box>
        )}
        {Math.ceil(paymentsTotal / 20) > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination
              count={Math.ceil(paymentsTotal / 20)}
              page={paymentsPage}
              onChange={(_, v) => setPaymentsPage(v)}
              color="primary"
            />
          </Box>
        )}
      </Card>

      {/* Confirm dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => !paying && setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Confirm Payment</DialogTitle>
        <DialogContent>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
            <Stack spacing={1.25}>
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Biller
                </Typography>
                <Typography variant="body2" fontWeight={600} textAlign="right">
                  {biller?.billerName}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Category
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {category?.label}
                </Typography>
              </Stack>
              {Object.entries(filledParams).map(([k, v]) => (
                <Stack key={k} direction="row" justifyContent="space-between" spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    {prettyParam(k)}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {v}
                  </Typography>
                </Stack>
              ))}
              {fetchedBill?.customerName && (
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    Customer
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {fetchedBill.customerName}
                  </Typography>
                </Stack>
              )}
              {pan && (
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    Payer PAN
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ fontFamily: "monospace" }}
                  >
                    {maskPan(pan)}
                  </Typography>
                </Stack>
              )}
              <Divider />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  {myCharge > 0 ? "Bill" : "Amount"}
                </Typography>
                <Typography fontWeight={myCharge > 0 ? 600 : 800} fontSize={myCharge > 0 ? 15 : 18}>
                  {formatINR(amount)}
                </Typography>
              </Stack>
              {myCharge > 0 && (
                <>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Convenience charge
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatINR(myCharge)}
                    </Typography>
                  </Stack>
                  <Divider />
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" fontWeight={700}>
                      Total
                    </Typography>
                    <Typography fontWeight={800} fontSize={18}>
                      {formatINR(Number(amount) + myCharge)}
                    </Typography>
                  </Stack>
                </>
              )}
            </Stack>
          </Paper>
          <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
            {myCharge > 0
              ? `${formatINR(Number(amount) + myCharge)} will be debited from your main wallet balance (incl. ${formatINR(myCharge)} charge).`
              : "The amount will be debited from your main wallet balance."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={paying}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePay}
            disabled={paying || !panOk}
            startIcon={
              paying ? <CircularProgress size={16} color="inherit" /> : null
            }
            sx={{ borderRadius: 2.5, fontWeight: 700, px: 3 }}
          >
            {paying ? "Processing…" : `Pay ${formatINR(amount)}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bill payment details dialog */}
      <Dialog
        open={!!detailPayment}
        onClose={() => setDetailPayment(null)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
          Bill Payment Details
        </DialogTitle>
        <DialogContent dividers>
          {detailPayment &&
            (() => {
              const p = detailPayment;
              const inputs = parseParams(p.input_params);
              const charges =
                p.total_commission && p.amount
                  ? `${((p.total_commission / p.amount) * 100).toFixed(
                      2,
                    )}% / ${formatINR(p.total_commission)}`
                  : "0% / ₹0";
              const customerName = `${p.customer_first_name || ""} ${
                p.customer_last_name || ""
              }`.trim();
              return (
                <Box>
                  <DetailRow
                    label="Transaction Ref"
                    value={p.txn_ref_id || "—"}
                    mono
                  />
                  <DetailRow
                    label="BBPS Ref"
                    value={p.bbps_ref_id || "—"}
                    mono
                  />
                  <DetailRow
                    label="Biller"
                    value={`${p.biller_name || p.biller_id || "—"}${
                      p.biller_id ? ` (${p.biller_id})` : ""
                    }`}
                  />
                  <DetailRow label="Category" value={p.category || "—"} />
                  <DetailRow label="Amount" value={formatINR(p.amount)} />
                  <DetailRow label="Charges" value={charges} />

                  {inputs.length > 0 && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        color="text.secondary"
                        mb={0.5}
                      >
                        Consumer Inputs
                      </Typography>
                      {inputs.map((ip, i) => (
                        <DetailRow
                          key={`${ip.paramName || i}-${i}`}
                          label={prettyParam(ip.paramName || "")}
                          value={ip.paramValue || "—"}
                        />
                      ))}
                    </>
                  )}

                  {(customerName ||
                    p.customer_mobile ||
                    p.customer_email) && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        color="text.secondary"
                        mb={0.5}
                      >
                        Customer
                      </Typography>
                      <DetailRow label="Name" value={customerName || "—"} />
                      <DetailRow
                        label="Mobile"
                        value={p.customer_mobile || "—"}
                      />
                      <DetailRow
                        label="Email"
                        value={p.customer_email || "—"}
                      />
                    </>
                  )}

                  <Divider sx={{ my: 1.5 }} />
                  <DetailRow
                    label="Status"
                    value={<StatusChip status={p.status} />}
                  />
                  {p.status === "failed" && p.failure_reason && (
                    <DetailRow
                      label="Failure Reason"
                      value={
                        <Typography variant="body2" color="error.main">
                          {p.failure_reason}
                        </Typography>
                      }
                    />
                  )}
                  <DetailRow
                    label="Date"
                    value={formatDate(p.created_at)}
                  />
                </Box>
              );
            })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDetailPayment(null)}
            sx={{ textTransform: "none" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
